// "Recommended for You" / "Recently Viewed" tabs at the bottom of product pages
// (sections/related-products.liquid, "tabs" layout). Recently viewed comes from this browser's
// history kept by wishlist.js; tiles match snippets/product-tile.liquid. A tab with nothing to
// show is hidden, and the whole section hides if both are empty.
if (!customElements.get('product-picks')) {
  customElements.define(
    'product-picks',
    class ProductPicks extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.recommendations = this.querySelector('product-recommendations');
        this.recentGrid = this.querySelector('[data-recent-grid]');

        this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab.dataset.tab)));
        this.querySelector('[role="tablist"]').addEventListener('keydown', (event) => this.onKey(event));

        // Recommendations arrive asynchronously. The theme only fetches them once they scroll into
        // view, which never happens while their tab is hidden, so start the fetch here and keep the
        // tab until it lands (or give up after a few seconds).
        if (this.recommendations) {
          this.recPending = true;
          const settle = () => {
            this.recPending = false;
            this.update();
          };
          new MutationObserver(settle).observe(this.recommendations, { childList: true, subtree: true });
          setTimeout(settle, 6000);
          this.recommendations.loadRecommendations?.(this.recommendations.dataset.productId);
        }
        this.renderRecent().then(() => this.update());
      }

      async renderRecent() {
        const store = window.CRWishlist;
        if (!store || !this.recentGrid) return;
        const handles = store.recent().filter((h) => h !== this.dataset.currentHandle).slice(0, 4);
        const products = (await Promise.all(handles.map((h) => store.product(h)))).filter(Boolean);
        this.recentGrid.replaceChildren(...products.map((p) => this.tile(p)));
      }

      tile(product) {
        const item = document.createElement('li');
        item.className = 'product-tile grid__item';
        const image = product.featured_image || product.images?.[0];
        const src = image ? `${image}${image.includes('?') ? '&' : '?'}width=900` : '';
        item.innerHTML = `
          <a class="product-tile__link" href="${product.url || `/products/${product.handle}`}">
            <span class="product-tile__media">${src ? `<img src="${src}" alt="" loading="lazy">` : ''}</span>
            <span class="product-tile__info">
              <span class="product-tile__title"></span>
              <span class="product-tile__price"></span>
            </span>
          </a>`;
        item.querySelector('.product-tile__title').textContent = product.title;
        item.querySelector('.product-tile__price').textContent = this.money(product.price);

        const heart = document.createElement('wishlist-button');
        heart.className = 'wishlist-heart product-tile__wishlist';
        heart.dataset.handle = product.handle;
        heart.dataset.title = product.title;
        heart.innerHTML = this.querySelector('template[data-heart]')?.innerHTML || '';
        item.appendChild(heart);
        return item;
      }

      update() {
        const has = {
          rec: !!this.recommendations?.querySelector('.product-tile') || !!this.recPending,
          recent: !!this.recentGrid?.children.length,
        };
        this.tabs.forEach((tab) => (tab.hidden = !has[tab.dataset.tab]));
        this.hidden = !has.rec && !has.recent;
        const current = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
        if (!current || current.hidden) {
          const first = this.tabs.find((t) => !t.hidden);
          if (first) this.select(first.dataset.tab);
        }
      }

      select(name, focus = false) {
        this.tabs.forEach((tab) => {
          const active = tab.dataset.tab === name;
          tab.setAttribute('aria-selected', active);
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        this.querySelectorAll('[role="tabpanel"]').forEach((panel) => (panel.hidden = panel.dataset.panel !== name));
      }

      onKey(event) {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        const visible = this.tabs.filter((t) => !t.hidden);
        const index = visible.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        const next = visible[(index + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length];
        if (next) this.select(next.dataset.tab, true);
      }

      money(cents) {
        if (typeof cents !== 'number') return '';
        const amount = cents / 100;
        const whole = Number.isInteger(amount);
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        }).format(amount);
      }
    }
  );
}
